import { Document, IActivity, PersonData } from '../../data-models';

export const UPLOAD_ACTIVITY_TYPE = 'UPLOAD';
export const FAILED_UPLOAD_STATUS = 'Failed';
export const PROCESSING_WINDOW_MS = 15 * 60 * 1000;

export type UploadStatus = 'processing';

/**
 * Document ids known to be available as real documents, either reported by
 * POST /refresh or already present in personData
 */
export interface KnownDocIds {
  availableDocIds: ReadonlySet<string>;
  documentFileNames: ReadonlySet<string>;
}

export interface UploadDocument extends Document {
  caseId: string;
  pending: true;
  uploadStatus?: UploadStatus;
}

const createdAt = (activity: IActivity) =>
  new Date(activity.createdDate).getTime();

/**
 * @returns ms since the upload was created (NaN if createdDate is invalid)
 */
export const uploadAge = (activity: IActivity, now: number) =>
  now - createdAt(activity);

export const isWithinProcessingWindow = (activity: IActivity, now: number) =>
  uploadAge(activity, now) < PROCESSING_WINDOW_MS;

export const isFailedUpload = (activity: IActivity) =>
  activity.isProcessed && activity.status === FAILED_UPLOAD_STATUS;

/**
 * Upload tracking (pending rows, labels, polling) only applies to uploads
 * made since it shipped. Older uploads report trackAvailability false.
 */
export const isTrackedUpload = (activity: IActivity) =>
  activity.type === UPLOAD_ACTIVITY_TYPE && activity.trackAvailability === true;

/**
 * @returns The case the file was actually uploaded to
 */
export const uploadCaseIdOf = (activity: IActivity) =>
  activity.uploadCaseId || activity.caseId;

/**
 * @returns The fileNames of every document and letter in personData
 */
export const getDocumentFileNames = (
  personData?: PersonData,
): ReadonlySet<string> => {
  const documents = [
    ...(personData?.documents?.letters ?? []),
    ...(personData?.documents?.documents ?? []),
  ];

  return new Set(
    documents
      .filter((document) => document.fileName != null)
      .map((document) => String(document.fileName)),
  );
};

/**
 * @returns true once the upload's document can be shown as a real document
 */
export const isUploadAvailable = (activity: IActivity, known: KnownDocIds) => {
  if (activity.docId == null) {
    return false;
  }
  const docId = String(activity.docId);

  return known.availableDocIds.has(docId) || known.documentFileNames.has(docId);
};

/**
 * @returns true if the upload should be listed alongside the documents. Failed
 * uploads never are; they're reported with a toast instead
 */
export const isVisibleUpload = (
  activity: IActivity,
  now: number,
  known: KnownDocIds,
) =>
  isTrackedUpload(activity) &&
  !isFailedUpload(activity) &&
  !isUploadAvailable(activity, known) &&
  (!activity.isProcessed ||
    (activity.docId != null && isWithinProcessingWindow(activity, now)));

export const getUploadStatus = (
  activity: IActivity,
  now: number,
): UploadStatus | undefined => {
  if (!isTrackedUpload(activity) || isFailedUpload(activity)) {
    return undefined;
  }
  if (isWithinProcessingWindow(activity, now)) {
    return 'processing';
  }
  return undefined;
};

/**
 * @returns When the upload's label or visibility next changes: when it leaves
 * the processing window
 */
const statusChangeAt = (activity: IActivity, now: number) =>
  isWithinProcessingWindow(activity, now)
    ? createdAt(activity) + PROCESSING_WINDOW_MS
    : undefined;

/**
 * @returns When the soonest of the uploads' labels or visibility changes
 */
export const nextStatusChangeAt = (
  activities: IActivity[],
  now: number,
): number | undefined => {
  const changes = activities
    .filter(
      (activity) => isTrackedUpload(activity) && !isFailedUpload(activity),
    )
    .map((activity) => statusChangeAt(activity, now))
    .filter((changeAt): changeAt is number => changeAt !== undefined);

  return changes.length ? Math.min(...changes) : undefined;
};

export const toUploadDocument = (
  activity: IActivity,
  now: number,
): UploadDocument => {
  const caseId = uploadCaseIdOf(activity);

  return {
    caseId,
    claimId: caseId,
    leaveId: caseId,
    receivedDate: activity.createdDate,
    name: activity.docType || 'Uploaded Document',
    pending: true,
    uploadStatus: getUploadStatus(activity, now),
  };
};
